import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  TemplateRef
} from '@angular/core';
import { Item, Field } from '@sitecore-jss/sitecore-jss/layout';
import {
  ToAccountsListForTransfersResponse,
  IBankAccountListItem,
  IBasicAddress
} from '../../../transfers/services/to-accounts.service';
import {
  trigger,
  style,
  transition,
  animate,
  AnimationEvent
} from '@angular/animations';
import { fromEvent, Observable, Subject } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { GetItemListFieldsService } from '../../services/get-item-list-fields.service';
import {
  ISecurityAddressBook,
  IAddressBookAction
} from '../../interfaces/address-book.interfaces';

enum ADDRESS_BOOK_TABS {
  Personal,
  Companies,
  Animation
}

const tabAnimation = [
  transition(':enter', [
    style({
      opacity: 0
    }),
    animate(800)
  ]),
  transition(':leave', [animate(200, style({ opacity: 0 }))])
];

@Component({
  selector: 'ab-address-book',
  templateUrl: './address-book.component.html',
  styleUrls: ['./address-book.component.scss'],
  animations: [
    trigger('changeTabPersonal', tabAnimation),
    trigger('changeTabCompanies', tabAnimation)
  ]
})
export class AddressBookComponent implements OnInit {
  @Input() personalTabNotPermission: TemplateRef<any>;
  @Input() internalTransferDisabled: TemplateRef<any>;
  @Input() externalTransferDisabled: TemplateRef<any>;
  @Input() billTransferDisabled: TemplateRef<any>;

  @Input() items: { [key: string]: Item };
  @Input() data: ToAccountsListForTransfersResponse;
  @Input() securityAddressBook: ISecurityAddressBook;
  @Input() pageMode: boolean;
  @Input() companiesActions: IAddressBookAction[];
  @Input() beneficiariesActions: IAddressBookAction[];
  @Output() onSelect = new EventEmitter<any>();
  @Output() addAddress = new EventEmitter<any>();
  @Output() tabChange = new EventEmitter<ADDRESS_BOOK_TABS>();

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  private _companiesList: { [key: string]: any[] } = {};
  private _ownAccountList: IBankAccountListItem[];
  private _beneficiariesList: { [key: string]: IBasicAddress[] } = {};
  private _searchSubject;

  public tabs: { [key: string]: Item };
  public addressBookTabs = ADDRESS_BOOK_TABS;
  public selectedTab: ADDRESS_BOOK_TABS = ADDRESS_BOOK_TABS.Personal;
  public search: string;
  public personalSearch: Field;
  public companiesSearch: Field;
  public ownAccountList: Observable<IBankAccountListItem[]>;
  public beneficiariesList: Observable<{ [key: string]: IBasicAddress[] }>;
  public companiesList: Observable<{ [key: string]: any[] }>;
  public showOwnAccounts = true;
  public isEmpty = false;
  public ownAccountListEmpty = false;
  public beneficiariesListEmpty = false;

  constructor(private getItemListFieldsService: GetItemListFieldsService) {}

  ngOnInit() {
    this.pageMode = !!this.pageMode;
    this.tabs = this.getItemListFieldsService.getItems(
      this.items.tabs.fields.items as Item[]
    );

    this.personalSearch = this.items.personalSearch.fields.label as Field;
    this.companiesSearch = this.items.companiesSearch.fields.label as Field;

    this.initPersonalList();
    this.initCompaniesList();

    this.ownAccountList = this.searchObservable.pipe(
      startWith(null),
      map(event => {
        const result = this.filterOwnAccounts(event);
        // setTimeout prevent console error for changeing the value
        setTimeout(() => {
          this.ownAccountListEmpty = result.length === 0;
        });

        return result;
      })
    );

    this.companiesList = this.searchObservable.pipe(
      startWith(null),
      map(event => {
        const result = this.filterLetterArray(event, this._companiesList);
        // setTimeout prevent console error for changeing the value
        setTimeout(() => {
          this.isEmpty = Object.keys(result).length === 0;
        });

        return result;
      })
    );

    this.beneficiariesList = this.searchObservable.pipe(
      startWith(null),
      map(event => {
        const result = this.filterLetterArray(event, this._beneficiariesList);
        // setTimeout prevent console error for changeing the value
        setTimeout(() => {
          this.beneficiariesListEmpty = Object.keys(result).length === 0;
        });

        return result;
      })
    );

    if (
      this.securityAddressBook.hideContacts &&
      this.securityAddressBook.hideOwnAccounts
    ) {
      this.selectedTab = ADDRESS_BOOK_TABS.Companies;
    }

    this.tabChange.emit(this.selectedTab);
  }

  selectTab(tab: ADDRESS_BOOK_TABS) {
    if (tab === this.selectedTab) {
      return;
    }

    this.selectedTab = this.isEmpty ? tab : ADDRESS_BOOK_TABS.Animation;
    if (this.isEmpty) {
      this.tabChange.emit(this.selectedTab);
    }

    this.isEmpty = false;
  }

  setOdd(i: number) {
    return i % 2 === 0;
  }

  select(item: any) {
    this.onSelect.emit(item);
  }

  trackByFn(index, item) {
    if (!item) {
      return null;
    }

    return item.accountNumber;
  }

  clearValue() {
    this.search = '';
    this._searchSubject.next();
  }

  captureDoneEvent(event: AnimationEvent, tab: ADDRESS_BOOK_TABS) {
    if (event.toState === 'void') {
      this.selectedTab = tab;
      this.tabChange.emit(this.selectedTab);
    }
  }

  triggerAddAddress() {
    this.addAddress.emit(true);
  }

  private initPersonalList() {
    let letter: string;

    if (this.data.beneficiariesAddresses) {
      this.data.beneficiariesAddresses.forEach(beneficiary => {
        if (
          !letter ||
          (beneficiary.name && letter !== beneficiary.name[0].toUpperCase())
        ) {
          letter = beneficiary.name[0].toUpperCase();
          this._beneficiariesList[letter] = [];
        }

        this._beneficiariesList[letter].push(beneficiary);
      });
    }

    if (this.data.ownBankAccounts) {
      this._ownAccountList = this.data.ownBankAccounts.sort((first, second) => {
        const firstName = this.getNameValue(first).toLocaleLowerCase();
        const secondName = this.getNameValue(second).toLocaleLowerCase();
        if (firstName > secondName) {
          return 1;
        }

        if (firstName < secondName) {
          return -1;
        }

        return 0;
      });

      this.showOwnAccounts =
        this._ownAccountList.length > 0 &&
        !this.securityAddressBook.hideOwnAccounts;
    }
  }

  private get searchObservable(): Observable<any> {
    if (!this._searchSubject) {
      this._searchSubject = new Subject();

      fromEvent<IBankAccountListItem[]>(
        this.searchInput.nativeElement,
        'input'
      ).subscribe(event => {
        this._searchSubject.next(event);
      });
    }

    return this._searchSubject.asObservable();
  }

  private initCompaniesList() {
    let letter: string;
    if (this.data.billPayeesCompanies) {
      this.data.billPayeesCompanies.forEach(company => {
        if (
          !letter ||
          (company.name && letter !== company.name[0].toUpperCase())
        ) {
          letter = company.name[0].toUpperCase();
          this._companiesList[letter] = [];
        }

        this._companiesList[letter].push(company);
      });
    }
  }

  private getNameValue(item): string {
    return item.name || item.alias || item.description;
  }

  private filterOwnAccounts(event) {
    if (!this.search) {
      return this._ownAccountList;
    }

    return this._ownAccountList.filter(account => {
      return (
        this.getNameValue(account)
          .toLocaleLowerCase()
          .indexOf(this.search.toLocaleLowerCase()) !== -1 ||
        account.accountNumber.indexOf(this.search) !== -1
      );
    });
  }

  private filterLetterArray(event, data: { [key: string]: any[] }) {
    if (!this.search) {
      return data;
    }

    const search = this.search.toLocaleLowerCase();

    return Object.keys(data).reduce((result, key) => {
      const filterdData = data[key].filter(item => {
        return (
          this.getNameValue(item)
            .toLocaleLowerCase()
            .indexOf(search) !== -1 ||
          item.accountNumber.toLocaleLowerCase().indexOf(search) !== -1 ||
          (item.alias && item.alias.toLocaleLowerCase().indexOf(search) !== -1)
        );
      });

      if (filterdData.length !== 0) {
        result[key] = filterdData;
      }

      return result;
    }, {});
  }
}

export { ADDRESS_BOOK_TABS };
